import React from "react";
import { Calendar, Repeat, Clock, CreditCard } from "lucide-react";
import MarketingPage, { MarketingSection, Pillar } from "@/apps/rider/components/MarketingPage";

export default function HowLinkWorks() {
  return (
    <MarketingPage
      eyebrow="How it works"
      title="A commute that runs on autopilot."
      lede="Link is scheduled, recurring, round-trip transportation. Set your route once and we'll run it on the days you choose — no surge, no haggling, no wondering whether a driver will accept."
      back="/"
      backLabel="Home"
      testId="how-link-works-page"
      cta={{ to: "/", label: "Schedule your route" }}
    >
      {/* The five pillars */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Pillar Icon={Calendar} title="Set your route once" testId="how-pillar-route">
          Pick your pickup and destination. Choose the days you commute. We build a recurring schedule on the spot — Monday through Friday, three days a week, whatever fits your life.
        </Pillar>
        <Pillar Icon={Repeat} title="Round-trip is included" testId="how-pillar-round-trip">
          Every booking is pickup <em>and</em> return — same driver pool, same predictable timing. You don't book two rides; you book your day.
        </Pillar>
        <Pillar Icon={Clock} title="Smart pickup timing" testId="how-pillar-timing">
          Tell us when you need to arrive. We work backwards: traffic, distance, your usual buffer. The pickup window appears in your dashboard with a live countdown.
        </Pillar>
        <Pillar Icon={CreditCard} title="Pay before service" testId="how-pillar-payment">
          Single-day rides charge once at confirmation. Weekly plans charge upfront, then renew every Monday — with a 48-hour heads-up before each renewal.
        </Pillar>
      </div>

      <MarketingSection eyebrow="The flow" title="What a typical week looks like" testId="how-flow">
        <p>
          Sunday night, you confirm next week's commute from the rider dashboard — same route as last week, charged automatically. Monday morning, the Next Ride card on your home screen counts down to pickup. Five minutes before, your phone buzzes. The driver arrives. You get to work, and at the end of the day, the same plan brings you home.
        </p>
        <p>
          No app-juggling. No fare comparisons. No driver-roulette.
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="What we are not" title="Not a rideshare." testId="how-not-rideshare">
        <p>
          Link doesn't accept on-demand rides, doesn't add stops, and doesn't surge-price during weather or events. We're built for the same trip you take three to five days a week — the one you'd love to stop thinking about.
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="Where we run" title="Kansas City metro, end to end." testId="how-coverage">
        <p>
          Today, Link operates across the entire Kansas City metro — Overland Park, Lee's Summit, Independence, Liberty, Blue Springs, Raymore, Belton, Gladstone, and the suburbs surrounding Union Station. Routes up to 50 miles one-way are supported. <a className="text-[#ed6500] underline-offset-4 hover:underline" href="/service-areas">See full service map →</a>
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="No surprise charges" title="Predictable, by design." testId="how-pricing-context">
        <p>
          Your daily rate is determined by your route, not by the time of day or weather. Once you've confirmed a route, the price is locked for as long as your subscription is active — no surge, no per-ride fees.
        </p>
      </MarketingSection>
    </MarketingPage>
  );
}
